// src/components/Contact.js
import React, { useRef, useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const formRef = useRef(null);
  const infoRef = useRef(null);

  const [contactData, setContactData] = useState({
    email: 'info@leaf-fb.com',
    phone: '+971 507 073 213',
    address: 'THREE LEAF FOOD & BEVERAGES TRADING L.L.C, DUBAI, UAE'
  });

  const [visibility, setVisibility] = useState({
    email: true,
    phone: true,
    address: true
  });

  const [lastUpdated, setLastUpdated] = useState(0);

  const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' ? window.location.origin : '');

  // EmailJS Configuration - Replace with your actual values
  const EMAILJS_CONFIG = {
    SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_wgc2tq7',
    TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_xxdtdd9',
    PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'sj3pzrzzSyAg5YnVd'
  };

  const fetchContactData = () => {
    // Fetch contact information from API
    fetch(API_BASE + '/backend/api.php/content?slug=contact')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        if (data && data.item && data.item.json) {
          const contactInfo = data.item.json;
          setContactData(contactInfo);
          setLastUpdated(Date.now());
        }
      })
      .catch(() => {});
  };

  const fetchVisibilitySettings = () => {
    // Fetch visibility settings
    fetch(API_BASE + '/backend/api.php/contact-visibility')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        if (data && data.visibility) {
          setVisibility(data.visibility);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    // Initial fetch
    fetchContactData();
    fetchVisibilitySettings();

    // Set up interval to refresh data every 10 seconds
    const intervalId = setInterval(() => {
      fetchContactData();
      fetchVisibilitySettings();
    }, 10000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [API_BASE]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
        }
      });
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (headingRef.current) observer.observe(headingRef.current);
    if (formRef.current) observer.observe(formRef.current);
    if (infoRef.current) observer.observe(infoRef.current);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      if (headingRef.current) observer.unobserve(headingRef.current);
      if (formRef.current) observer.unobserve(formRef.current);
      if (infoRef.current) observer.unobserve(infoRef.current);
    };
  }, []);

  const [submitState, setSubmitState] = useState({ 
    loading: false, 
    ok: false, 
    error: '',
    phpSuccess: false,
    emailjsSuccess: false 
  });

  // Send email via EmailJS
  const sendViaEmailJS = async (formData) => {
    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        message: formData.message,
        to_email: 'info@leaf-fb.com',
        website: 'leaf-fb.com',
        submission_date: new Date().toLocaleString()
      };

      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      return { success: true, response: result };
    } catch (error) {
      console.error('EmailJS error:', error);
      return { success: false, error: error.text || 'EmailJS service failed' };
    }
  };

  // Send email via PHP backend
  const sendViaPHP = async (formData) => {
    try {
      const res = await fetch(API_BASE + '/backend/api.php/contact-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'PHP backend failed');
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formRef.current) return;
    
    const form = new FormData(formRef.current);
    const payload = {
      name: form.get('name') || '',
      email: form.get('email') || '',
      phone: form.get('phone') || '',
      message: form.get('message') || ''
    };

    setSubmitState({ 
      loading: true, 
      ok: false, 
      error: '',
      phpSuccess: false,
      emailjsSuccess: false 
    });

    try {
      // Send both emails simultaneously using Promise.allSettled
      const [phpResult, emailjsResult] = await Promise.allSettled([
        sendViaPHP(payload),
        sendViaEmailJS(payload)
      ]);

      const phpSuccess = phpResult.status === 'fulfilled' && phpResult.value.success;
      const emailjsSuccess = emailjsResult.status === 'fulfilled' && emailjsResult.value.success;

      // Check results
      if (phpSuccess || emailjsSuccess) {
        // At least one method succeeded
        setSubmitState({ 
          loading: false, 
          ok: true, 
          error: '',
          phpSuccess,
          emailjsSuccess
        });
        formRef.current.reset();
        
        // Log which methods worked for debugging
        if (phpSuccess && emailjsSuccess) {
          console.log('✅ Both PHP and EmailJS methods succeeded');
        } else if (phpSuccess) {
          console.log('✅ PHP method succeeded, EmailJS failed');
        } else {
          console.log('✅ EmailJS method succeeded, PHP failed');
        }
      } else {
        // Both methods failed
        const phpError = phpResult.status === 'rejected' ? phpResult.reason : 
                        (phpResult.value?.error || 'PHP method failed');
        const emailjsError = emailjsResult.status === 'rejected' ? emailjsResult.reason : 
                           (emailjsResult.value?.error || 'EmailJS method failed');
        
        const errorMessage = `Both methods failed: PHP - ${phpError}, EmailJS - ${emailjsError}`;
        
        setSubmitState({ 
          loading: false, 
          ok: false, 
          error: 'Failed to send message. Please try again later.',
          phpSuccess: false,
          emailjsSuccess: false
        });
        
        console.error('❌ Both email methods failed:', { phpError, emailjsError });
      }
      
    } catch (err) {
      // This catch block should rarely be hit due to Promise.allSettled
      setSubmitState({ 
        loading: false, 
        ok: false, 
        error: 'Unexpected error occurred. Please try again.',
        phpSuccess: false,
        emailjsSuccess: false
      });
      console.error('Unexpected error in handleSubmit:', err);
    }
  };

  // Helper function to display success details
  const renderSuccessMessage = () => {
    if (!submitState.ok) return null;

    let message = 'Message sent successfully';
    
    return (
      <p style={{ color: '#059669', marginTop: '0.75rem' }}>
        {message}
      </p>
    );
  };

  return (
    <section id="contact" className="section contact-section" ref={sectionRef}>
      <div className="container">
        <h2 className="section-title animate-on-scroll slide-in-left" ref={headingRef}>Contact Us</h2>
        <div className="contact-content">
          {/* Contact Form */}
          <form 
            className="contact-form animate-on-scroll slide-in-left delay-1" 
            ref={formRef}
            onSubmit={handleSubmit}
          >
            <h3>Let's grow together.</h3>
            <p>Reach out to our team for product inquiries, custom solutions, or partnership opportunities.</p>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input type="text" id="name" name="name" required />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input type="email" id="email" name="email" required />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input type="tel" id="phone" name="phone" />
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" rows="5" required></textarea>
            </div>
            <button type="submit" className="btn" disabled={submitState.loading}>
              {submitState.loading ? 'Sending...' : 'Send Message'}
            </button>
            
            {renderSuccessMessage()}
            
            {submitState.error && (
              <p style={{ color: '#dc2626', marginTop: '0.75rem' }}>{submitState.error}</p>
            )}
            
            {/* Debug info - you can remove this in production */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#6b7280' }}>
                <div>PHP: {submitState.phpSuccess ? '✅' : '❌'}</div>
                <div>EmailJS: {submitState.emailjsSuccess ? '✅' : '❌'}</div>
              </div>
            )}
          </form>

          {/* Contact Info */}
          <div className="contact-info animate-on-scroll slide-in-right delay-1" ref={infoRef}>
            <h3>Contact Information</h3>
            {visibility.email && (
              <div className="info-item">
                <div className="info-icon">
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div className="info-content">
                  <h4>Email</h4>
                  <a href={`mailto:${contactData.email}`}>{contactData.email}</a>
                </div>
              </div>
            )}
            {visibility.phone && (
              <div className="info-item">
                <div className="info-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" />
                  </svg>
                </div>
                <div className="info-content">
                  <h4>WhatsApp</h4>
                  <a 
                    href={`https://wa.me/${contactData.phone.replace(/\D/g, '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {contactData.phone}
                  </a>
                </div>
              </div>
            )}
            {visibility.address && (
              <div className="info-item">
                <div className="info-icon">
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="info-content">
                  <h4>Address</h4>
                  <p>{contactData.address}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .contact-content {
          display: flex;
          gap: 3rem;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .contact-form, .contact-info {
          flex: 1;
          background: #ffffff;
          padding: 2.5rem;
          border-radius: 16px;
          box-shadow: 0 8px 25px rgba(0,0,0,0.08);
        }
        
        .contact-form h3 {
          color: #5FA32E;
          margin-bottom: 1rem;
          font-size: 1.5rem;
        }
        
        .contact-form p {
          color: #6b7280;
          margin-bottom: 2rem;
          line-height: 1.6;
        }
        
        .form-group {
          margin-bottom: 1.5rem;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          color: #374151;
          font-weight: 500;
        }
        
        .form-group input, .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }
        
        .form-group input:focus, .form-group textarea:focus {
          outline: none;
          border-color: #7AC943;
          box-shadow: 0 0 0 3px rgba(122, 201, 67, 0.1);
        }
        
        .btn {
          background: linear-gradient(135deg, #7AC943, #5FA32E);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
        }
        
        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(122, 201, 67, 0.3);
        }
        
        .contact-info h3 {
          color: #5FA32E;
          margin-bottom: 2rem;
          font-size: 1.5rem;
        }
        
        .info-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 2rem;
        }
        
        .info-icon {
          flex-shrink: 0;
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #7AC943, #5FA32E);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.5rem;
          color: white;
        }
        
        .info-content h4 {
          color: #374151;
          margin-bottom: 0.5rem;
          font-size: 1.1rem;
          font-weight: 600;
        }
        
        .info-content a, .info-content p {
          color: #6b7280;
          text-decoration: none;
          transition: color 0.3s ease;
          line-height: 1.5;
        }
        
        .info-content a:hover {
          color: #5FA32E;
        }
        
        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .contact-content {
            flex-direction: column;
            gap: 2rem;
            padding: 0 1rem;
          }
          
          .contact-form, .contact-info {
            width: 100%;
            margin: 0 auto;
            padding: 2rem;
            box-sizing: border-box;
          }
          
          .contact-form {
            order: 1;
          }
          
          .contact-info {
            order: 2;
          }
          
          .form-group input, .form-group textarea {
            padding: 0.875rem;
          }
          
          .btn {
            padding: 0.875rem 1.5rem;
          }
          
          .info-item {
            flex-direction: row;
            align-items: center;
          }
          
          .info-icon {
            width: 48px;
            height: 48px;
            margin-right: 1.2rem;
          }
        }
        
        @media (max-width: 480px) {
          .contact-form, .contact-info {
            padding: 1.5rem;
            border-radius: 12px;
          }
          
          .contact-form h3, .contact-info h3 {
            font-size: 1.3rem;
          }
          
          .info-item {
            flex-direction: row;
            margin-bottom: 1.5rem;
          }
          
          .info-icon {
            width: 44px;
            height: 44px;
            margin-right: 1rem;
          }
          
          .info-icon svg {
            width: 24px;
            height: 24px;
          }
          
          .info-content h4 {
            font-size: 1rem;
          }
        }
      `}</style>
    </section>
  );
};

export default Contact;